import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

/**
 * PSA-Style Geoportal (Barangay-level) — React 18 single-file component
 *
 * Features:
 * - Loads barangay boundaries from a GeoJSON URL
 * - Filters by Province, Municipality/City, Barangay; free-text search
 * - Click to view attributes; zoom to selection
 * - Basemap switcher (OSM, ESRI World Imagery)
 * - Sidebar with controls + attribute table (paginated)
 * - Export filtered features to GeoJSON or CSV
 *
 * Usage:
 * <PSAGeoportal dataUrl="/data/barangays.geojson" height="80vh" />
 *
 * Expected properties for each feature (case-insensitive fallback):
 *   REGION / NAME_1, PROVINCE / NAME_2, MUNICITY / NAME_3, BRGY_NAME / NAME_4
 */

function getField(props, candidates) {
  for (const key of candidates) {
    if (props[key] != null) return props[key];
    const found = Object.keys(props).find(
      (k) => k.toLowerCase() === key.toLowerCase()
    );
    if (found) return props[found];
  }
  return "";
}

function toPlainProps(props) {
  return {
    region: getField(props, ["REGION", "NAME_1", "region"]),
    province: getField(props, ["PROVINCE", "NAME_2", "prov_name", "province"]),
    muncity: getField(props, ["MUNICITY", "NAME_3", "citymun", "municipality", "city"]),
    brgy: getField(props, ["BRGY_NAME", "NAME_4", "barangay", "brgy", "bgy_name"]),
    psgc: getField(props, ["PSGC", "psgc", "PSGC_CODE"]),
  };
}

function hashColor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  const r = (h >>> 16) & 0xff;
  const g = (h >>> 8) & 0xff;
  const b = h & 0xff;
  return `rgba(${(r + 128) % 256}, ${(g + 128) % 256}, ${(b + 128) % 256}, 0.5)`;
}

function bboxOfFeature(f) {
  const coords = [];
  const walk = (g) => {
    const { type, coordinates } = g;
    if (type === "Polygon") coords.push(...coordinates.flat());
    else if (type === "MultiPolygon")
      coordinates.forEach((poly) => coords.push(...poly.flat()));
  };
  if (f.geometry) walk(f.geometry);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  coords.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });
  return [[minY, minX], [maxY, maxX]]; // Leaflet uses [lat, lng]
}

function FitToBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && isFinite(bounds[0][0])) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds]);
  return null;
}

export default function PSAGeoportal({ dataUrl, height = "85vh" }) {
  const [raw, setRaw] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // selected feature
  const [bounds, setBounds] = useState(null);

  // Filters
  const [province, setProvince] = useState("");
  const [muncity, setMuncity] = useState("");
  const [barangay, setBarangay] = useState("");
  const [query, setQuery] = useState("");

  // UI
  const [tablePage, setTablePage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    async function load() {
      try {
        setError("");
        const res = await fetch(dataUrl);
        if (!res.ok) throw new Error(`Failed to fetch ${dataUrl}`);
        const gj = await res.json();
        if (gj.type !== "FeatureCollection") throw new Error("Invalid GeoJSON");
        setRaw(gj);
      } catch (e) {
        setError(String(e));
      }
    }
    load();
  }, [dataUrl]);

  const features = useMemo(() => (raw?.features ?? []).map((f) => ({
    ...f,
    __plain: toPlainProps(f.properties || {}),
  })), [raw]);

  const unique = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();

  const provinces = useMemo(
    () => unique(features.map((f) => f.__plain.province)),
    [features]
  );
  const muncities = useMemo(() => {
    const filtered = province
      ? features.filter((f) => f.__plain.province === province)
      : features;
    return unique(filtered.map((f) => f.__plain.muncity));
  }, [features, province]);
  const barangays = useMemo(() => {
    const filtered = features.filter((f) =>
      (!province || f.__plain.province === province) &&
      (!muncity || f.__plain.muncity === muncity)
    );
    return unique(filtered.map((f) => f.__plain.brgy));
  }, [features, province, muncity]);

  const filteredFeatures = useMemo(() => {
    const q = query.trim().toLowerCase();
    return features.filter((f) => {
      const p = f.__plain;
      const passProv = !province || p.province === province;
      const passCity = !muncity || p.muncity === muncity;
      const passBgy = !barangay || p.brgy === barangay;
      const passQ = !q ||
        [p.province, p.muncity, p.brgy, p.region, p.psgc]
          .join(" ")
          .toLowerCase()
          .includes(q);
      return passProv && passCity && passBgy && passQ;
    });
  }, [features, province, muncity, barangay, query]);

  const filteredCollection = useMemo(
    () => ({ type: "FeatureCollection", features: filteredFeatures }),
    [filteredFeatures]
  );

  useEffect(() => {
    setTablePage(1);
  }, [province, muncity, barangay, query]);

  const totalPages = Math.max(1, Math.ceil(filteredFeatures.length / pageSize));
  const pageItems = filteredFeatures.slice((tablePage - 1) * pageSize, tablePage * pageSize);

  const onFeatureClick = (f) => {
    setSelected(f);
    const b = bboxOfFeature(f);
    setBounds(b);
  };

  const styleFn = (feature) => {
    const p = toPlainProps(feature.properties || {});
    return {
      color: "#222",
      weight: 0.8,
      fillColor: hashColor(p.muncity || p.province || ""),
      fillOpacity: 0.5,
    };
  };

  const onEachFeature = (feature, layer) => {
    const p = toPlainProps(feature.properties || {});
    const html = `
      <div class="space-y-1">
        <div class="font-semibold text-sm">${p.brgy || "(No name)"}</div>
        <div class="text-xs">${p.muncity || ""}${p.muncity && p.province ? ", " : ""}${p.province || ""}</div>
        ${p.psgc ? `<div class="text-[11px] text-gray-600">PSGC: ${p.psgc}</div>` : ""}
      </div>`;
    layer.bindPopup(html);
    layer.on("click", () => onFeatureClick(feature));
  };

  function download(filename, text) {
    const blob = new Blob([text], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  const exportGeoJSON = () => {
    download(
      `barangays_filtered_${Date.now()}.geojson`,
      JSON.stringify(filteredCollection)
    );
  };

  const exportCSV = () => {
    const rows = filteredFeatures.map((f) => toPlainProps(f.properties || {}));
    const headers = ["region", "province", "muncity", "brgy", "psgc"];
    const csv = [headers.join(",")]
      .concat(
        rows.map((r) =>
          headers
            .map((h) => `"${String(r[h] ?? "").replaceAll("\"", '"')}"`)
            .join(",")
        )
      )
      .join("\n");
    download(`barangays_filtered_${Date.now()}.csv`, csv);
  };

  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-3">
      {/* Sidebar */}
      <aside className="bg-white rounded-2xl shadow p-4 space-y-4">
        <div className="text-xl font-semibold">PSA-Style Geoportal</div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-xs text-gray-600">Province</label>
            <select
              className="w-full border rounded-xl p-2"
              value={province}
              onChange={(e) => {
                setProvince(e.target.value);
                setMuncity("");
                setBarangay("");
              }}
            >
              <option value="">All</option>
              {provinces.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Municipality/City</label>
            <select
              className="w-full border rounded-xl p-2"
              value={muncity}
              onChange={(e) => {
                setMuncity(e.target.value);
                setBarangay("");
              }}
            >
              <option value="">All</option>
              {muncities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Barangay</label>
            <select
              className="w-full border rounded-xl p-2"
              value={barangay}
              onChange={(e) => setBarangay(e.target.value)}
            >
              <option value="">All</option>
              {barangays.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Search (PSGC / name)</label>
            <input
              type="text"
              placeholder="Type to filter…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full border rounded-xl p-2"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button className="px-3 py-2 rounded-xl bg-gray-100" onClick={() => { setProvince(""); setMuncity(""); setBarangay(""); setQuery(""); }}>Clear</button>
            <button className="px-3 py-2 rounded-xl bg-gray-100" onClick={exportGeoJSON}>Export GeoJSON</button>
            <button className="px-3 py-2 rounded-xl bg-gray-100" onClick={exportCSV}>Export CSV</button>
          </div>
        </div>

        <div className="pt-2">
          <div className="text-sm font-medium mb-2">Results ({filteredFeatures.length.toLocaleString()})</div>
          <div className="max-h-[40vh] overflow-auto border rounded-xl">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="text-left p-2 border-b">Barangay</th>
                  <th className="text-left p-2 border-b">City/Mun</th>
                  <th className="text-left p-2 border-b">Province</th>
                  <th className="text-left p-2 border-b">PSGC</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((f, i) => {
                  const p = f.__plain;
                  return (
                    <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => onFeatureClick(f)}>
                      <td className="p-2 border-b">{p.brgy}</td>
                      <td className="p-2 border-b">{p.muncity}</td>
                      <td className="p-2 border-b">{p.province}</td>
                      <td className="p-2 border-b">{p.psgc}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2 text-sm">
            <div>Page {tablePage} / {totalPages}</div>
            <div className="flex gap-2">
              <button className="px-2 py-1 rounded border" disabled={tablePage === 1} onClick={() => setTablePage((p) => Math.max(1, p - 1))}>Prev</button>
              <button className="px-2 py-1 rounded border" disabled={tablePage === totalPages} onClick={() => setTablePage((p) => Math.min(totalPages, p + 1))}>Next</button>
            </div>
          </div>
        </div>

        {selected && (
          <div className="mt-3 p-3 border rounded-xl bg-gray-50">
            <div className="font-medium mb-1">Selected</div>
            <div className="text-sm space-y-1">
              <div><span className="text-gray-600">Barangay:</span> {toPlainProps(selected.properties).brgy}</div>
              <div><span className="text-gray-600">City/Mun:</span> {toPlainProps(selected.properties).muncity}</div>
              <div><span className="text-gray-600">Province:</span> {toPlainProps(selected.properties).province}</div>
              {toPlainProps(selected.properties).psgc && (
                <div><span className="text-gray-600">PSGC:</span> {toPlainProps(selected.properties).psgc}</div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 p-3 border border-red-300 bg-red-50 text-red-700 rounded-xl text-sm">
            {String(error)}
          </div>
        )}
      </aside>

      {/* Map */}
      <div className="rounded-2xl overflow-hidden shadow" style={{ height }}>
        <MapContainer center={[12.8797, 121.774]} zoom={6} style={{ height: "100%", width: "100%" }}>
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OSM Standard">
              <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="ESRI World Imagery">
              <TileLayer attribution='Tiles &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* Filtered GeoJSON */}
          {filteredCollection.features.length > 0 && (
            <GeoJSON data={filteredCollection} style={styleFn} onEachFeature={onEachFeature} />
          )}

          {/* Fit to bounds when user clicks on a row/feature */}
          <FitToBounds bounds={bounds} />
        </MapContainer>
      </div>
    </div>
  );
}
